import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import useSWR from 'swr'
import { TickerAvatar, TickerItem, TickerOut, countdownWords, isShown, itemWords } from '../ticker/logic'
import { Fluent } from '../ui'

/**
 * A thin bar under the site header: live CADINUdex trades first ("bezbm bought $250 of CADINU · 10s ago", green buys, red sells), then verified MultiSwap
 * savings ("Saved 0.42% via Biswap · 2m ago", with that exchange's logo), competition, farm and staking events, then big 24 h moves; scrolling slowly. Every row is a link. The times are written in the browser from the trade's own time, so they keep
 * counting between refreshes. It stops while it is hovered, focused or touched, scrolls by hand (no animation) with prefers-reduced-motion, and is not shown at
 * all when there is nothing real to show (the home page keeps its height).
 * Logos (Bez 2026-10-07): every row starts with a round 20 px coin, the token's logo (a saving: its exchange's), lit from the top left and
 * ringed in the row's colour; a letter in the same coin when no logo loads, so nothing moves. Each competition running now is its own
 * capsule right after the live trades ("CADINU competition live · 200,000,000 CADINU prize · ends in 9d 10h"), its logo in the green live
 * ring of the Competitions tab with one breathing pulse (none under reduced motion); it goes when its countdown runs out.
 */
const scroll = keyframes`from { transform: translateX(0); } to { transform: translateX(-50%); }`
const Bar = styled.div`
  position: relative;
  overflow: hidden;
  margin: 0 0 12px;
  border-radius: 999px;
  border: 1px solid rgba(57, 255, 160, 0.24);
  background: linear-gradient(90deg, rgba(57, 255, 160, 0.08), rgba(127, 212, 232, 0.06));
  mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 4%, #000 96%, transparent);
  .track {
    display: inline-flex;
    white-space: nowrap;
    will-change: transform;
    animation: ${scroll} var(--dur, 40s) linear infinite;
  }
  &:hover .track,
  &:focus-within .track,
  &[data-paused='true'] .track {
    animation-play-state: paused;
  }
  @media (prefers-reduced-motion: reduce) {
    overflow-x: auto;
    mask-image: none;
    -webkit-mask-image: none;
    .track {
      animation: none;
    }
    .dup {
      display: none;
    }
  }
`
const COLOR: Record<string, string> = { buy: '#39ffa0', up: '#39ffa0', sell: '#ff7b8a', down: '#ff7b8a', event: '#f6c453' }
/** the coin's rim in the row's colour (rgb, for alpha) */
const RIM: Record<string, string> = { buy: '57, 255, 160', up: '57, 255, 160', sell: '255, 123, 138', down: '255, 123, 138', event: '246, 196, 83' }
/** theme.colors.success (dark), the Competitions tab's live ring */
const LIVE = '57, 217, 138'
export const AVATAR_PX = 20

const Row = styled(Link)<{ $tone: string }>`
  display: inline-flex;
  align-items: center;
  /* rows line up by their box: a letter coin has a text baseline, a logo coin has none */
  vertical-align: top;
  min-height: 36px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  color: ${({ $tone }) => COLOR[$tone] ?? '#e6ecff'};
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  &:focus-visible {
    outline: none;
  }
  /* the words sit inside a flex chip, where the link's own underline does not reach */
  &:hover .words,
  &:focus-visible .words {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  &:focus-visible .chip {
    border-radius: 999px;
    box-shadow: 0 0 0 2px rgba(223, 233, 255, 0.7);
  }
  &::after {
    content: '';
    width: 4px;
    height: 4px;
    margin-left: 14px;
    border-radius: 50%;
    background: rgba(180, 200, 255, 0.35);
  }
  /* a competition running now: a lit capsule, green rim, the countdown in white tabular figures */
  &[data-kind='live'] {
    padding: 0 10px;
    color: #f6c453;
    .chip {
      height: 30px;
      padding: 0 13px 0 5px;
      border-radius: 999px;
      background: linear-gradient(180deg, rgba(${LIVE}, 0.22) 0%, rgba(${LIVE}, 0.07) 100%), rgba(10, 14, 34, 0.6);
      box-shadow: inset 0 0 0 1px rgba(${LIVE}, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 3px 10px rgba(0, 0, 0, 0.45), 0 0 16px rgba(${LIVE}, 0.16);
    }
    &:focus-visible .chip {
      box-shadow: inset 0 0 0 1px rgba(${LIVE}, 0.5), 0 0 0 2px rgba(223, 233, 255, 0.8);
    }
    .clock {
      color: #f2fff8;
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
  }
`

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  70%, 100% { transform: scale(1.4); opacity: 0; }
`
/** The row's coin: a fixed 20 px disc in every state (loading, logo, letter), so a logo arriving or failing never moves the text. */
const Disc = styled.span<{ $rim: string }>`
  position: relative;
  flex: none;
  display: inline-grid;
  place-items: center;
  width: ${AVATAR_PX}px;
  height: ${AVATAR_PX}px;
  border-radius: 50%;
  background: radial-gradient(120% 120% at 30% 20%, #2a3a86 0%, #150f4a 60%, #0a0e22 100%);
  box-shadow: 0 0 0 1.5px rgba(${({ $rim }) => $rim}, 0.6), 0 2px 5px rgba(0, 0, 0, 0.55);
  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    display: block;
    transition: opacity 180ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .letter {
    font-size: 12px;
    font-weight: 800;
    line-height: 1;
    color: #dfe9ff;
  }
  /* lit from the top left: a coin, not a sticker */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: linear-gradient(155deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 46%);
    pointer-events: none;
  }
  /* the Competitions tab's live ring: green, a dark hairline, the live glow and a drop */
  &[data-live='true'] {
    box-shadow: 0 0 0 2px rgb(${LIVE}), 0 0 0 3.5px #0b0f2a, 0 0 10px 2px rgba(${LIVE}, 0.45), 0 3px 6px 1px rgba(0, 0, 0, 0.5);
  }
  .pulse {
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    border: 2px solid rgba(${LIVE}, 0.9);
    pointer-events: none;
    animation: ${pulse} 2s cubic-bezier(0.22, 1, 0.36, 1) infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    img {
      transition: none;
    }
    .pulse {
      animation: none;
      opacity: 0;
    }
  }
`

/**
 * The row's picture: the logo URLs in order (one that fails is skipped), the letter when none is left, the farm icon for a farm period.
 * While a logo loads the coin stays empty (no letter flashing into a logo).
 */
export function TickerCoin({ avatar, tone }: { avatar: TickerAvatar; tone: string }) {
  const srcs = avatar.srcs ?? []
  const key = srcs.join('|')
  const [i, setI] = useState(0)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    setI(0)
    setLoaded(false)
  }, [key])
  const src = srcs[i]
  const state = src ? (loaded ? 'image' : 'loading') : 'letter'
  return (
    <Disc $rim={RIM[tone] ?? RIM.event} data-testid="v3-ticker-avatar" data-state={state} data-letter={avatar.letter} data-live={avatar.live ? 'true' : undefined} aria-hidden="true">
      {state === 'letter' && (avatar.icon ? <Fluent name={avatar.icon} size={14} /> : <span className="letter">{avatar.letter}</span>)}
      {src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          key={src}
          src={src}
          alt=""
          width={AVATAR_PX}
          height={AVATAR_PX}
          decoding="async"
          referrerPolicy="no-referrer"
          style={{ opacity: loaded ? 1 : 0 }}
          onLoad={() => setLoaded(true)}
          onError={() => {
            setLoaded(false)
            setI((n) => n + 1)
          }}
        />
      )}
      {avatar.live && <span className="pulse" />}
    </Disc>
  )
}

const iconOf = (it: TickerItem) => (it.kind === 'trade' ? 'bolt' : it.kind === 'mover' ? (it.tone === 'down' ? 'barchart' : 'chart') : /^(farm|pool):/.test(it.id) ? 'moneybag' : 'trophy')

/** Shown on the competitions list, a competition and the token pages; never on Create, Referral or the lock-claim form (nothing to follow there). */
const SHOW_ON = ['/trading-competition-v3', '/trading-competition-v3/[id]', '/trading-competition-v4', '/trading-competition-v4/[id]', '/tokens', '/token/[address]'] // page routes (the public URLs are rewritten to these)

/** The same bar, empty: holds the ticker's height while it loads (and when there is nothing to show) so nothing under it moves. */
const Shell = styled(Bar)`
  height: 38px;
  margin: 0;
  span {
    display: inline-flex;
    align-items: center;
    height: 36px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    color: rgba(223, 233, 255, 0.55);
  }
`

/**
 * `reserve` (the home page): the bar always takes its 38 px, so the hero under it never jumps when the ticker arrives a moment after the page; while it
 * loads, or when there is nothing to show, an empty bar stands in its place.
 */
export default function TickerBar({ force, reserve }: { force?: boolean; reserve?: boolean }) {
  const { pathname } = useRouter()
  const allowed = force || SHOW_ON.includes(pathname)
  const { data } = useSWR<TickerOut>(allowed ? '/api/competition-v3/ticker' : null, (u: string) => fetch(u).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status))))), {
    refreshInterval: 20_000,
    revalidateOnFocus: true,
    shouldRetryOnError: false,
    dedupingInterval: 10_000,
  })
  const [touch, setTouch] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(timer.current), [])
  // "10s ago" keeps counting between refreshes; null until mounted, so the server and the first paint agree
  const [now, setNow] = useState<number | null>(null)
  useEffect(() => {
    setNow(Math.floor(Date.now() / 1000))
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 10_000)
    return () => clearInterval(t)
  }, [])
  // a live competition whose countdown ran out in the browser goes (the server's answer can be a minute old)
  const items = (data?.items ?? []).filter((it) => isShown(it, now))
  if (!allowed) return null
  if (!items.length) return reserve ? <Shell aria-hidden="true" data-testid="v3-ticker-reserve"><span>{data ? '' : 'Live activity'}</span></Shell> : null
  const pause = () => {
    setTouch(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setTouch(false), 6000)
  }
  // a live competition's countdown in its own figures ("· ends in 9d 10h"); every other row one line of words
  const words = (it: TickerItem) => {
    if (now === null || !it.id) return it.text
    const cd = it.kind === 'live' ? countdownWords(it, now) : null
    if (!cd) return itemWords(it, now)
    return (
      <>
        {it.text} · <span className="clock">{cd}</span>
      </>
    )
  }
  // the whole list twice, so the loop has no gap; roughly 5 s per row
  const rows = (dup: boolean) => items.map((it) => (
    <Row key={`${dup ? 'b' : 'a'}${it.id ?? `${it.kind}${it.href}${it.text}`}`} href={it.href} $tone={it.tone ?? 'event'} tabIndex={dup ? -1 : undefined} data-testid="v3-ticker-row" data-kind={it.kind} data-tone={it.tone} data-at={it.at}>
      <span className="chip">
        {it.avatar ? (
          <TickerCoin avatar={it.avatar} tone={it.tone ?? 'event'} />
        ) : it.logo ? (
          <Image src={it.logo} alt="" width={16} height={16} unoptimized style={{ flex: 'none', borderRadius: '50%' }} />
        ) : (
          <Fluent name={iconOf(it)} size={16} />
        )}
        <span className="words">{words(it)}</span>
      </span>
    </Row>
  ))
  return (
    <Bar role="region" aria-label="Live activity" data-testid="v3-ticker" data-paused={touch} onTouchStart={pause} style={{ ['--dur' as string]: `${Math.max(24, items.length * 7)}s`, ...(reserve ? { height: 38, margin: 0 } : {}) }}>
      <div className="track">
        <span>{rows(false)}</span>
        <span className="dup" aria-hidden="true">{rows(true)}</span>
      </div>
    </Bar>
  )
}
